import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/types";
import {
  CompanyHealthData,
  CompanyDetail,
  CoachingDashboardState,
} from "@/modules/coaching/types";
import { getCoachCompanies, GetCoachCompaniesParams } from "@/api/coaching/getCoachCompanies";
import { getCompanyMetrics } from "@/api/coaching/getCompanyMetrics";
import { logEngagement as logEngagementApi } from "@/api/coaching/logEngagement";

/**
 * Initial state for coaching dashboard
 */
const initialState: CoachingDashboardState = {
  companies: [],
  loading: false,
  error: null,

  // Filters
  filters: {
    sortBy: 'health',
    filterBy: 'all',
    searchQuery: '',
  },

  // Selected company for detail view
  selectedCompanyId: null,
  selectedCompanyDetail: null,
  selectedCompanyLoading: false,
  selectedCompanyError: null,

  // Admin view
  isAdminView: false,
  selectedCoachUserId: null,
};

/**
 * Coaching Dashboard Redux Slice
 */
const coachingDashboardSlice = createSlice({
  name: "coachingDashboard",
  initialState,
  reducers: {
    // Company list actions
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    setCompanies: (state, action: PayloadAction<CompanyHealthData[]>) => {
      state.companies = action.payload;
    },

    // Filter actions
    setSortBy: (
      state,
      action: PayloadAction<'health' | 'lastEngagement' | 'companyName' | 'reminders'>
    ) => {
      state.filters.sortBy = action.payload;
    },

    setFilterBy: (
      state,
      action: PayloadAction<'all' | 'needsAttention' | 'noRecentEngagement'>
    ) => {
      state.filters.filterBy = action.payload;
    },

    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.filters.searchQuery = action.payload;
    },

    // Selected company detail actions
    setSelectedCompanyId: (state, action: PayloadAction<number | null>) => {
      state.selectedCompanyId = action.payload;
    },

    setSelectedCompanyLoading: (state, action: PayloadAction<boolean>) => {
      state.selectedCompanyLoading = action.payload;
    },

    setSelectedCompanyError: (state, action: PayloadAction<string | null>) => {
      state.selectedCompanyError = action.payload;
    },

    setSelectedCompanyDetail: (state, action: PayloadAction<CompanyDetail | null>) => {
      state.selectedCompanyDetail = action.payload;
    },

    clearSelectedCompany: (state) => {
      state.selectedCompanyId = null;
      state.selectedCompanyDetail = null;
      state.selectedCompanyLoading = false;
      state.selectedCompanyError = null;
    },

    // Admin view actions
    setIsAdminView: (state, action: PayloadAction<boolean>) => {
      state.isAdminView = action.payload;
    },

    setSelectedCoachUserId: (state, action: PayloadAction<number | null>) => {
      state.selectedCoachUserId = action.payload;
    },

    // Reset state
    resetDashboard: () => initialState,
  },
});

// Export actions
export const {
  setLoading,
  setError,
  setCompanies,
  setSortBy,
  setFilterBy,
  setSearchQuery,
  setSelectedCompanyId,
  setSelectedCompanyLoading,
  setSelectedCompanyError,
  setSelectedCompanyDetail,
  clearSelectedCompany,
  setIsAdminView,
  setSelectedCoachUserId,
  resetDashboard,
} = coachingDashboardSlice.actions;

// ============================================================================
// Async Thunks (Placeholder implementations)
// ============================================================================

/**
 * Fetch companies for coach dashboard
 */
export const fetchCoachCompanies =
  (params?: GetCoachCompaniesParams): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response = await getCoachCompanies(params);
      dispatch(setCompanies(response.companies));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch companies"));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

/**
 * Fetch detailed company metrics
 */
export const fetchCompanyDetail =
  (companyId: number): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setSelectedCompanyId(companyId));
    dispatch(setSelectedCompanyLoading(true));
    dispatch(setSelectedCompanyError(null));
    try {
      const response = await getCompanyMetrics(companyId);

      // Convert API response to CompanyDetail format
      // CompanyDetail extends CompanyHealthData, so we need all those fields
      const companyDetail: CompanyDetail = {
        id: response.companyId,
        companyName: '', // Will be fetched separately or from state
        intacctId: null,
        serviceTitanTenantId: null,
        healthScore: response.healthScore,
        healthIndicator: response.healthIndicator,
        lastEngagement: null,
        lastEngagementCoach: null,
        activeRemindersCount: 0,
        latestNote: null,
        metrics: response.metrics,
        notes: [], // Will be populated when notes feature is implemented
        reminders: [], // Will be populated when reminders feature is implemented
        engagementEvents: [], // Will be populated when engagement history is implemented
        metricsHistory: [] // Will be populated when history charts are implemented
      };

      dispatch(setSelectedCompanyDetail(companyDetail));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setSelectedCompanyError(error.message));
      } else {
        dispatch(setSelectedCompanyError("Failed to fetch company details"));
      }
    } finally {
      dispatch(setSelectedCompanyLoading(false));
    }
  };

/**
 * Log engagement with company
 * Uses optimistic update for lastEngagement instead of refetching all companies.
 */
export const logCoachEngagement =
  (companyId: number, eventType: 'call' | 'email' | 'meeting' | 'general', eventNotes?: string): AppThunk =>
  async (dispatch: AppDispatch, getState) => {
    // Optimistic update: set lastEngagement to now for the target company
    const companies = getState().coachingDashboard.companies;
    const updatedCompanies = companies.map((c) =>
      c.id === companyId ? { ...c, lastEngagement: new Date() } : c
    );
    dispatch(setCompanies(updatedCompanies));

    try {
      await logEngagementApi({ companyId, eventType, eventNotes });
    } catch (error) {
      // Revert optimistic update on failure
      dispatch(setCompanies(companies));
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to log engagement"));
      }
    }
  };

// Export reducer
export default coachingDashboardSlice.reducer;
